/**
 * ApplicationModal component
 * Multi-step modal form for job application submission
 */
import React, { useEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useApplicationFormStore } from '../../stores/application-form-store'
import { ProgressIndicator } from './ProgressIndicator'
import { CandidateInfoForm } from './CandidateInfoForm'
import { CvUploadForm } from './CvUploadForm'
import { ReviewStep } from './ReviewStep'
import { SuccessMessage } from './SuccessMessage'
import { useCreateApplication } from '../../hooks/useCreateApplication'
import { useTranslation } from '../../hooks/useTranslation'
import { markJobAsApplied } from '../../lib/applied-jobs'
import type { CreateApplicationRequest } from '../../types'

export const ApplicationModal: React.FC = () => {
  const {
    isModalOpen,
    currentStep,
    closeModal,
    reset,
    selectedJobId,
    candidateFullName,
    candidateEmail,
    candidatePhone,
    candidateLinkedIn,
    cvFileKey,
    cvFileName,
    cvMimeType,
    recaptchaToken
  } = useApplicationFormStore()
  const { t } = useTranslation()
  const [showSuccess, setShowSuccess] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const createApplicationMutation = useCreateApplication()

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isModalOpen])

  // Reset success state when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setShowSuccess(false)
      setSubmissionError(null)
    }
  }, [isModalOpen])

  /**
   * Handle application submission — uses reCAPTCHA token from store (validated on Apply click)
   */
  const handleSubmit = async () => {
    if (!selectedJobId || !candidateFullName || !candidateEmail || !cvFileKey) {
      setSubmissionError(t('application.submit.error.missingInfo'))
      return
    }

    if (!recaptchaToken) {
      setSubmissionError('reCAPTCHA verification failed. Please close and try again.')
      return
    }

    setSubmissionError(null)

    const payload: CreateApplicationRequest = {
      jobPostingId: selectedJobId,
      candidateEmail: candidateEmail,
      candidatePhone: candidatePhone,
      candidateFullName: candidateFullName,
      candidateLinkedIn: candidateLinkedIn || undefined,
      cvFileKey: cvFileKey!,
      cvFileName: cvFileName || '',
      cvMimeType: cvMimeType || '',
      source: 'website',
      recaptchaToken
    }

    try {
      await createApplicationMutation.mutateAsync(payload)
      markJobAsApplied(selectedJobId)
      setShowSuccess(true)
    } catch (error: unknown) {
      const apiError = error as { statusCode?: number; message?: string }
      if (apiError.statusCode === 400) {
        if (apiError.message?.includes('already applied')) {
          setSubmissionError(t('application.submit.error.duplicate'))
        } else {
          setSubmissionError(apiError.message || t('application.submit.error.invalidData'))
        }
      } else if (apiError.statusCode === 404) {
        setSubmissionError(t('application.submit.error.notAvailable'))
      } else if (apiError.statusCode && apiError.statusCode >= 500) {
        setSubmissionError(t('application.submit.error.server'))
      } else {
        setSubmissionError(t('application.submit.error.generic'))
      }
    }
  }

  const handleSuccessClose = () => {
    setShowSuccess(false)
    reset()
  }

  return (
    <Dialog.Root open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          aria-hidden="true"
        />

        <Dialog.Content
          className="
            fixed z-50
            left-1/2 top-1/2
            -translate-x-1/2 -translate-y-1/2
            w-full max-w-full h-full
            md:max-w-2xl md:h-auto md:max-h-[90vh]
            bg-card
            rounded-none md:rounded-lg
            shadow-xl
            flex flex-col
            transition-all duration-300
            data-[state=open]:animate-in
            data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0
            data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95
            data-[state=open]:zoom-in-95
          "
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          onCloseAutoFocus={() => {
            const applyButton = document.querySelector('[data-apply-button]') as HTMLButtonElement
            applyButton?.focus()
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-card-border">
            <Dialog.Title
              id="modal-title"
              className="text-xl md:text-2xl font-semibold font-heading text-card-text"
            >
              {t('application.modal.title')}
            </Dialog.Title>

            <Dialog.Close
              className="
                w-10 h-10
                flex items-center justify-center
                rounded-full
                hover:bg-gray-100
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
              "
              aria-label={t('application.modal.closeAriaLabel')}
            >
              <svg
                className="w-6 h-6 text-card-text-secondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Dialog.Close>
          </div>

          <Dialog.Description id="modal-description" className="sr-only">
            {t('application.modal.description')}
          </Dialog.Description>

          {/* Progress indicator */}
          {!showSuccess && (
            <div className="p-4 md:p-6 border-b border-card-border">
              <ProgressIndicator currentStep={currentStep} />
            </div>
          )}

          {/* Body content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {showSuccess ? (
              <SuccessMessage onClose={handleSuccessClose} />
            ) : (
              <>
                {currentStep === 1 && (
                  <div>
                    <h3 className="text-lg font-medium font-heading text-card-text mb-4">{t('application.steps.contactInfo')}</h3>
                    <CandidateInfoForm />
                  </div>
                )}

                {currentStep === 2 && (
                  <div>
                    <h3 className="text-lg font-medium font-heading text-card-text mb-4">{t('application.steps.uploadCv')}</h3>
                    <CvUploadForm />
                  </div>
                )}

                {currentStep === 3 && (
                  <div>
                    <h3 className="text-lg font-medium font-heading text-card-text mb-4">{t('application.steps.review')}</h3>
                    <ReviewStep />

                    {submissionError && (
                      <div
                        className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                        role="alert"
                      >
                        <div className="flex items-start gap-3">
                          <svg
                            className="w-5 h-5 text-error flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <p className="text-sm text-red-800">{submissionError}</p>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex justify-between">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="
                          px-6 py-3
                          border border-card-border
                          rounded-lg
                          font-medium text-card-text-secondary
                          hover:bg-gray-50
                          transition-colors
                          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                          min-h-[44px]
                        "
                      >
                        {t('common.actions.cancel')}
                      </button>

                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={createApplicationMutation.isPending}
                        className="
                          px-8 py-3
                          bg-primary text-surface
                          rounded-lg
                          font-medium
                          hover:bg-primary-hover
                          transition-colors
                          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                          disabled:opacity-50 disabled:cursor-not-allowed
                          flex items-center gap-2
                          min-h-[44px]
                        "
                        aria-label={createApplicationMutation.isPending ? t('application.review.submittingAriaLabel') : t('application.review.submitAriaLabel')}
                      >
                        {createApplicationMutation.isPending && (
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                        {createApplicationMutation.isPending ? t('application.review.submittingButton') : t('application.review.submitButton')}
                      </button>
                    </div>

                    <p className="mt-3 text-xs text-card-text-secondary text-center">
                      Protected by reCAPTCHA.{' '}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a>
                      {' '}&{' '}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
